/**
 * Authorization-card grid for the Candid RPA Auth Letter header tab.
 *
 * Sidenav tabs are UTC `created_at` dates; each tab shows that day's
 * flattened authorization payloads.
 *
 * @module candidRpaStatusContent
 */
import { SidebarTabPanel } from "@risalabs_frontend_org/oasis-ui-kit";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { CandidWritebackRecord } from "../../../pages/worklists/singleOrder/candidUpload/types/candidWriteback";
import { groupAuthorizationWritebacksByCreatedDate } from "../../../pages/worklists/singleOrder/candidUpload/utils/groupAuthorizationWritebacksByCreatedDate";
import { CandidWritebacksByUseCase } from "../../../pages/worklists/singleOrder/candidUpload/utils/groupCandidWritebacksByUseCase";
import { RootState } from "../../../redux/store/store";
import { toUtcDateKey } from "../../../utils/formatDate";
import AuthorizationCardInfo from "../authLetterSuccessModal/components/authorizationCardInfo";
import { mapAuthorizationPayloadToCard } from "./mapAuthorizationWritebacksToCards";

/**
 * Props for {@link CandidRpaStatusContent}.
 */
interface CandidRpaStatusContentProps {
  /** Header tab that owns this panel; cards render when it is `auth-letter`. */
  selectedNavItem: string;
}

/**
 * Reads authorization write-backs from modal metadata.
 *
 * @param state - Redux root state
 * @returns Authorization write-back rows, or an empty list
 */
const selectAuthorizationWritebacks = (
  state: RootState,
): CandidWritebacksByUseCase["authorization"] => {
  const grouped = state.modal.metaData?.groupedByUseCase as
    CandidWritebacksByUseCase | undefined;
  return grouped?.authorization ?? [];
};

/**
 * Reads the write-back row that opened the modal, when present.
 *
 * @param state - Redux root state
 * @returns The clicked table row, or undefined
 */
const selectRowData = (state: RootState): CandidWritebackRecord | undefined =>
  (state.modal.metaData?.rowData as CandidWritebackRecord | undefined) ??
  undefined;

/**
 * Picks the sidenav tab to open: the clicked row's UTC `created_at` date
 * when that tab exists, otherwise the oldest date tab.
 *
 * @param selectedRow - Write-back that opened the modal, if any
 * @param dateTabs - Tabs built from grouped authorization write-backs
 * @returns Tab `dateKey`, or an empty string when there are no tabs
 */
const resolveDefaultDateTab = (
  selectedRow: CandidWritebackRecord | undefined,
  dateTabs: { dateKey: string }[],
): string => {
  const rowDateKey = toUtcDateKey(selectedRow?.created_at);
  if (rowDateKey && dateTabs.some((tab) => tab.dateKey === rowDateKey)) {
    return rowDateKey;
  }
  return dateTabs[0]?.dateKey ?? "";
};

/**
 * Sidebar dates plus the authorization-card grid for the active day.
 *
 * @param props.selectedNavItem - Active header tab from the parent
 * @returns Sidebar tab panel keyed by write-back date
 */
const CandidRpaStatusContent = ({
  selectedNavItem,
}: CandidRpaStatusContentProps) => {
  const authorizationRecords = useSelector(selectAuthorizationWritebacks);
  const selectedRow = useSelector(selectRowData);
  const dateTabs = useMemo(
    () =>
      groupAuthorizationWritebacksByCreatedDate(authorizationRecords).map(
        (group) => ({
          dateKey: group.dateKey,
          label: group.label,
          cards: group.items.map((item, index) =>
            mapAuthorizationPayloadToCard(item.record, item.payload, index),
          ),
        }),
      ),
    [authorizationRecords],
  );
  const defaultTab = useMemo(
    () => resolveDefaultDateTab(selectedRow, dateTabs),
    [selectedRow, dateTabs],
  );

  return (
    <div className="content min-h-0 flex-1 overflow-hidden">
      <SidebarTabPanel
        activeTextColor="#0056D6"
        activeBorderColor="#0056D6"
        className="candid-rpa-status-content"
        sidebarWidth="25%"
        defaultValue={defaultTab}
        tabs={dateTabs.map((tab) => ({
          id: tab.dateKey,
          label: tab.label,
        }))}
      >
        {(activeTabId) => {
          if (selectedNavItem !== "auth-letter") {
            return null;
          }
          const activeTab = dateTabs.find((tab) => tab.dateKey === activeTabId);
          return (
            <div className="grid grid-cols-2 gap-4 overflow-auto p-3 pb-4">
              {(activeTab?.cards ?? []).map((authorization, index) => (
                <AuthorizationCardInfo
                  key={`${authorization.header.title}-${index}`}
                  header={authorization.header}
                  details={authorization.details}
                  serviceCodes={authorization.serviceCodes}
                />
              ))}
            </div>
          );
        }}
      </SidebarTabPanel>
    </div>
  );
};

export default CandidRpaStatusContent;
